import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Check, ArrowRightLeft, Calendar,
  Lock, Crown, Sparkles, RefreshCw
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as ReTooltip
} from 'recharts';
import { useSpaceStore } from '../stores/spaceStore';
import { useExpenseStore } from '../stores/expenseStore';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../lib/supabase';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Skeleton, { SkeletonList } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import BottomSheet from '../components/ui/BottomSheet';
import ProUpgradeModal from '../components/ProUpgradeModal';
import ExpenseEditSheet from '../components/ExpenseEditSheet';
import toast from 'react-hot-toast';
import { TOAST } from 'pairtab-shared/constants';
import type { RecentActivityItem } from '../types/activity';

const PIE_COLORS = [
  '#00C896', '#7C3AED', '#F5A623', '#FF4757', '#3B82F6',
  '#EC4899', '#14B8A6', '#F97316', '#8B5CF6', '#06B6D4'
];

interface CategorySpending {
  name: string;
  emoji: string;
  total_cents: number;
}

interface FixedOverviewItem {
  category_id: string;
  name: string;
  emoji: string;
  amount_cents: number;
  due_day: number;
  autopay: boolean;
  is_paused: boolean;
}

interface MemberBalance {
  id: string;
  display_name: string;
  avatar_emoji: string;
  balance_cents: number;
}

interface SharedStats {
  total_spent_cents: number;
  fixed_spent_cents: number;
  variable_spent_cents: number;
  member_balances: MemberBalance[];
  is_settled: boolean;
  category_spending: CategorySpending[];
  fixed_overview: FixedOverviewItem[];
  recent_activity: RecentActivityItem[];
}

interface InsightData {
  id?: string;
  type: string;
  title?: string;
  message: string;
  priority?: 'high' | 'medium' | 'low';
  confidence?: number;
  timeframe?: string;
}

export default function SharedDashboard() {
  const navigate = useNavigate();
  const { currentSpace, members } = useSpaceStore();
  const { settlements, createSettlement, subscribeToChanges, expenses } = useExpenseStore();
  const { isPro, user } = useAuthStore();

  const [stats, setStats] = useState<SharedStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettleSheet, setShowSettleSheet] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any | null>(null);

  // AI Insights state
  const [insights, setInsights] = useState<InsightData[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsCached, setInsightsCached] = useState(false);
  const [insightsLastUpdated, setInsightsLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!currentSpace) return;

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`/api/dashboard/shared/${currentSpace.id}`, {
          headers: { 'Authorization': `Bearer ${session?.access_token}` }
        });

        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [currentSpace, expenses, settlements]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (currentSpace?.id) {
      const unsub = subscribeToChanges(currentSpace.id);
      return () => unsub();
    }
  }, [currentSpace?.id]);

  // Load AI insights for Pro users
  const fetchInsights = async (_force = false) => {
    if (!isPro || !currentSpace) return;
    setInsightsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/insights/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ space_id: currentSpace.id, force: _force })
      });
      if (res.ok) {
        const data = await res.json();
        setInsights(data.insights || []);
        setInsightsCached(data.cached);
        setInsightsLastUpdated(data.last_updated || null);
      }
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    if (isPro && currentSpace) fetchInsights();
  }, [isPro, currentSpace]);

  const formatCurrency = (cents: number) =>
    `${cents < 0 ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartColors = {
    foreground: 'rgb(var(--color-foreground))',
    muted: 'rgb(var(--color-foreground) / 0.82)',
    tooltipText: 'rgb(var(--color-foreground))',
    border: 'rgb(var(--color-border) / 0.9)',
    card: 'rgb(var(--color-card))',
  };

  const getMemberName = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    return member?.display_name || 'Unknown';
  };

  const getMemberEmoji = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    return member?.avatar_emoji || '👤';
  };

  // Settle-up logic
  const owesMembers = useMemo(
    () => (stats?.member_balances || []).filter(m => m.balance_cents < 0),
    [stats?.member_balances]
  );
  const owedMembers = useMemo(
    () => (stats?.member_balances || []).filter(m => m.balance_cents > 0),
    [stats?.member_balances]
  );
  const myMemberId = useMemo(
    () => members.find((member) => member.user_id === user?.id)?.id || null,
    [members, user?.id]
  );

  const handleSettleUp = async () => {
    if (!stats || stats.is_settled || owesMembers.length === 0 || owedMembers.length === 0) return;

    try {
      const amount = Math.min(
        Math.abs(owesMembers[0].balance_cents),
        owedMembers[0].balance_cents
      );

      await createSettlement({
        space_id: currentSpace!.id,
        from_member: owesMembers[0].id,
        to_member: owedMembers[0].id,
        amount_cents: amount,
        note: 'Settlement'
      });

      toast.success(TOAST.settlementLogged);
      setShowSettleSheet(false);
    } catch (err: any) {
      toast.error(err.message || TOAST.errorGeneric);
    }
  };

  // Pie chart data
  const pieData = useMemo(
    () => (stats?.category_spending || [])
      .slice(0, 8)
      .map(c => ({ name: c.name, value: c.total_cents / 100 })),
    [stats?.category_spending]
  );

  // Active fixed overview
  const activeFixed = useMemo(
    () => (stats?.fixed_overview || []).filter(f => !f.is_paused),
    [stats?.fixed_overview]
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton width="100%" height={100} className="rounded-2xl" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton width="100%" height={80} />
          <Skeleton width="100%" height={80} />
        </div>
        <SkeletonList count={3} />
      </div>
    );
  }

  if (!currentSpace) {
    return (
      <EmptyState
        icon="👥"
        title="No Space Yet"
        description="Create a space to see shared dashboard"
        action={{ label: 'Create Space', onClick: () => navigate('/onboarding/space') }}
      />
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="p-2 -ml-2 rounded-full hover:bg-muted/10 touch-target"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold">Shared Dashboard</h1>
      </div>

      {/* Total Spending Card */}
      <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-muted text-sm">Total Shared Spending</p>
            <p className="text-2xl font-bold tabular-nums">
              {formatCurrency(stats?.total_spent_cents || 0)}
            </p>
          </div>
        </div>

        {/* Variable vs Fixed split */}
        {(stats?.total_spent_cents || 0) > 0 && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="bg-background/50 rounded-xl px-3 py-2 text-center">
              <p className="text-xs text-muted">Variable</p>
              <p className="text-sm font-semibold tabular-nums">
                {formatCurrency(stats?.variable_spent_cents || 0)}
              </p>
            </div>
            <div className="bg-background/50 rounded-xl px-3 py-2 text-center">
              <p className="text-xs text-muted">Fixed</p>
              <p className="text-sm font-semibold tabular-nums text-fixed">
                {formatCurrency(stats?.fixed_spent_cents || 0)}
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Balance Cards */}
      {stats?.member_balances && stats.member_balances.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold">Balances</h2>
          {stats.member_balances.map((member, i) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className={member.balance_cents === 0 ? 'border-primary/30' : ''}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{member.avatar_emoji}</span>
                    <span className="font-medium">{member.display_name}</span>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold tabular-nums ${
                      member.balance_cents > 0 ? 'text-primary' :
                      member.balance_cents < 0 ? 'text-danger' : 'text-muted'
                    }`}>
                      {member.balance_cents === 0 ? 'Even' :
                        `${member.balance_cents > 0 ? '+' : ''}${formatCurrency(member.balance_cents)}`}
                    </p>
                    {member.balance_cents !== 0 && (
                      <p className="text-xs text-muted">
                        {member.balance_cents > 0 ? 'is owed' : 'owes'}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Settle Up */}
      {!stats?.is_settled && owesMembers.length > 0 && owedMembers.length > 0 && (
        <Button onClick={() => setShowSettleSheet(true)} className="w-full">
          <ArrowRightLeft className="w-5 h-5 mr-2" />
          Settle Up
        </Button>
      )}

      {stats?.is_settled && (
        <Card className="bg-primary/10 border-primary/30">
          <div className="flex items-center justify-center gap-2 text-primary">
            <Check className="w-5 h-5" />
            <span className="font-medium">All settled up!</span>
          </div>
        </Card>
      )}

      {/* Fixed Expenses Overview */}
      {activeFixed.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-fixed" />
            Fixed Expenses
          </h2>
          <div className="space-y-2">
            {activeFixed.map(fixed => {
              const today = new Date().getDate();
              const due = fixed.due_day;
              const daysUntil = due >= today ? due - today : 31 - today + due;

              return (
                <Card key={fixed.category_id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{fixed.emoji}</span>
                      <div>
                        <p className="font-medium text-sm">{fixed.name}</p>
                        <p className="text-xs text-muted">
                          Due day {fixed.due_day}
                          {daysUntil <= 3 && (
                            <span className="text-danger ml-1">
                              · {daysUntil === 0 ? 'Today' : `in ${daysUntil}d`}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold tabular-nums text-sm">
                        {formatCurrency(fixed.amount_cents)}
                      </p>
                      <Badge variant={fixed.autopay ? 'split' : 'default'} size="sm">
                        {fixed.autopay ? 'AUTO' : 'MANUAL'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Category Pie Chart — Pro Gated */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Spending by Category</h2>
          {!isPro && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="flex items-center gap-1 text-xs text-yellow-400 font-medium"
            >
              <Crown size={12} /> PRO
            </button>
          )}
        </div>

        {isPro ? (
          pieData.length > 0 ? (
            <Card>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={75}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieData.map((_entry, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip
                    contentStyle={{
                      backgroundColor: chartColors.card,
                      border: `1px solid ${chartColors.border}`,
                      borderRadius: '12px',
                      fontSize: 13,
                      color: chartColors.tooltipText,
                    }}
                    labelStyle={{ color: chartColors.tooltipText, fontWeight: 600 }}
                    itemStyle={{ color: chartColors.tooltipText }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, '']}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="grid grid-cols-2 gap-2 mt-2">
                {(stats?.category_spending || []).slice(0, 8).map((cat, i) => (
                  <div key={cat.name} className="flex items-center gap-2 text-xs">
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="truncate text-sm text-foreground/85">{cat.emoji} {cat.name}</span>
                    <span className="font-semibold tabular-nums ml-auto text-foreground">
                      {formatCurrency(cat.total_cents)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <Card className="py-8">
              <p className="text-center text-muted text-sm">No spending data yet</p>
            </Card>
          )
        ) : (
          <Card
            interactive
            onClick={() => setShowUpgradeModal(true)}
            className="relative overflow-hidden"
          >
            <div className="h-[200px] flex flex-col items-center justify-center">
              <Lock className="w-6 h-6 text-muted mb-2" />
              <p className="font-medium text-sm">Category Breakdown Chart</p>
              <p className="text-xs text-muted">Upgrade to Pro to unlock</p>
            </div>
          </Card>
        )}
      </div>

      {/* AI Insights — Pro Gated */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            AI Insights
          </h2>
          {isPro && (
            <button
              onClick={() => fetchInsights(true)}
              disabled={insightsLoading}
              className="flex items-center gap-1 text-xs text-primary font-medium disabled:opacity-50"
            >
              <RefreshCw size={12} className={insightsLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          )}
        </div>

        {isPro ? (
          insightsLoading && insights.length === 0 ? (
            <div className="space-y-2">
              <Skeleton width="100%" height={60} className="rounded-xl" />
              <Skeleton width="100%" height={60} className="rounded-xl" />
              <Skeleton width="100%" height={60} className="rounded-xl" />
            </div>
          ) : insights.length > 0 ? (
            <div className="space-y-2">
              {insights.map((insight, i) => {
                const icons: Record<string, string> = {
                  pattern: '📊',
                  comparison: '↔️',
                  anomaly: '⚠️',
                  concentration: '🎯',
                  savings: '💡',
                  fixed_cost: '📌',
                };
                return (
                  <motion.div
                    key={insight.id || insight.title || i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Card className="py-3">
                      <div className="flex gap-3">
                        <span className="text-lg flex-shrink-0">{icons[insight.type] || '✨'}</span>
                        <div className="space-y-1">
                          {insight.title && (
                            <p className="text-[11px] uppercase tracking-[0.18em] text-muted">
                              {insight.title}
                            </p>
                          )}
                          <p className="text-sm">{insight.message}</p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
              {insightsCached && insightsLastUpdated && (
                <p className="text-[10px] text-muted text-center">
                  Last updated {formatDate(insightsLastUpdated)}
                </p>
              )}
            </div>
          ) : (
            <Card className="py-6">
              <p className="text-center text-muted text-sm">
                Log some expenses to generate insights
              </p>
            </Card>
          )
        ) : (
          <Card
            interactive
            onClick={() => setShowUpgradeModal(true)}
            className="relative overflow-hidden"
          >
            <div className="py-6 flex flex-col items-center justify-center gap-2">
              <Sparkles className="w-6 h-6 text-yellow-400/50" />
              <p className="font-medium text-sm">AI-Powered Insights</p>
              <p className="text-xs text-muted">Upgrade to Pro to unlock</p>
            </div>
          </Card>
        )}
      </div>

      {/* Recent Activity Feed */}
      <div className="space-y-3">
        <h2 className="font-semibold">Activity Feed</h2>
        {stats?.recent_activity && stats.recent_activity.length > 0 ? (
          <div className="space-y-2">
            {stats.recent_activity.map((activity, i) => {
              const canEditExpense = activity.type === 'expense' && activity.logged_by === myMemberId;
              return (
              <motion.div
                key={`${activity.type}-${activity.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
              >
                <Card
                  className="py-3"
                  interactive={canEditExpense}
                  onClick={canEditExpense ? () => setEditingExpense(activity) : undefined}
                >
                  {activity.type === 'expense' ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{activity.category?.emoji || '📦'}</span>
                        <div>
                          <p className="text-sm font-medium">
                            {activity.category?.name || 'Uncategorized'}
                          </p>
                          <p className="text-[10px] text-muted">
                            {getMemberEmoji(activity.paid_by)} {getMemberName(activity.paid_by)} · {formatDate(activity.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums text-sm">
                          {formatCurrency(activity.amount_cents)}
                        </p>
                        <Badge
                          variant={activity.is_fixed_autolog ? 'fixed' : activity.action === 'split' ? 'split' : 'keep'}
                          size="sm"
                        >
                          {activity.is_fixed_autolog ? 'FIXED' : activity.action.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <span className="text-lg">{activity.from_member_details.avatar_emoji}</span>
                          <ArrowRightLeft size={14} className="text-primary" />
                          <span className="text-lg">{activity.to_member_details.avatar_emoji}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {activity.from_member_details.display_name} paid {activity.to_member_details.display_name}
                          </p>
                          <p className="text-[10px] text-muted">
                            Settlement · {formatDate(activity.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold tabular-nums text-sm text-primary">
                          {formatCurrency(activity.amount_cents)}
                        </p>
                        <Badge variant="split" size="sm">SETTLED</Badge>
                      </div>
                    </div>
                  )}
                </Card>
              </motion.div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon="📝"
            title="No Activity Yet"
            description="Start logging expenses to see activity here"
            action={{ label: 'Log Expense', onClick: () => navigate('/log') }}
          />
        )}
      </div>

      {/* Settle Up Confirmation Sheet */}
      <BottomSheet
        isOpen={showSettleSheet}
        onClose={() => setShowSettleSheet(false)}
        title="Settle Up"
      >
        <div className="space-y-4">
          {owesMembers.length > 0 && owedMembers.length > 0 && (
            <div className="text-center space-y-3">
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <span className="text-3xl">{owesMembers[0].avatar_emoji}</span>
                  <p className="text-sm font-medium mt-1">{owesMembers[0].display_name}</p>
                </div>
                <div className="flex flex-col items-center">
                  <ArrowRightLeft className="w-5 h-5 text-primary" />
                  <p className="text-lg font-bold tabular-nums text-primary mt-1">
                    {formatCurrency(Math.min(
                      Math.abs(owesMembers[0].balance_cents),
                      owedMembers[0].balance_cents
                    ))}
                  </p>
                </div>
                <div className="text-center">
                  <span className="text-3xl">{owedMembers[0].avatar_emoji}</span>
                  <p className="text-sm font-medium mt-1">{owedMembers[0].display_name}</p>
                </div>
              </div>
              <p className="text-sm text-muted">
                {owesMembers[0].display_name} pays {owedMembers[0].display_name}
              </p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="secondary" onClick={() => setShowSettleSheet(false)}>
              Cancel
            </Button>
            <Button onClick={handleSettleUp}>
              Confirm
            </Button>
          </div>
        </div>
      </BottomSheet>

      <ProUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgradeMonthly={() => {}}
        onUpgradeYearly={() => {}}
      />

      <ExpenseEditSheet
        expense={editingExpense}
        isOpen={!!editingExpense}
        onClose={() => setEditingExpense(null)}
      />
    </div>
  );
}
